/*
 * GardenPack buy box behavior (loaded by sections/st_product-information.liquid).
 *
 * 1. <gp-gift-options>: "Is this a gift?" + message, stored as cart attributes
 *    ("Gift order", "Gift message") through /cart/update.js. Cart attributes are
 *    copied onto the order, where staff see them under "Additional details", and
 *    they survive any add-to-cart route (theme form, sticky bar or bundle app).
 * 2. Price per variety line: recomputed from the buy box price whenever it changes.
 * 3. Seasonal bundle labels: date-gated server side; applied here to bundle cards
 *    found with the merchant-configured selector.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  if (!customElements.get('gp-gift-options')) {
    class GpGiftOptions extends HTMLElement {
      connectedCallback() {
        this.check = this.querySelector('[data-gp-gift-check]');
        this.message = this.querySelector('[data-gp-gift-message]');
        this.panel = this.check && document.getElementById(this.check.getAttribute('aria-controls'));
        this.status = this.querySelector('[data-gp-gift-status]');
        this.count = this.querySelector('[data-gp-gift-count]');
        if (!this.check || !this.message || !this.panel) return;

        this.abort = new AbortController();
        const { signal } = this.abort;
        this.check.addEventListener('change', () => { this.render(); this.queueSave(0); }, { signal });
        this.message.addEventListener('input', () => { this.updateCount(); this.setStatus(''); this.queueSave(600); }, { signal });
        this.restore();
      }

      disconnectedCallback() {
        this.abort?.abort();
        this.request?.abort();
        clearTimeout(this.timer);
      }

      render() {
        const on = this.check.checked;
        this.panel.hidden = !on;
        this.check.setAttribute('aria-expanded', String(on));
        this.updateCount();
      }

      updateCount() {
        if (!this.count) return;
        const template = this.dataset.msgCount || '[count] of [max] characters';
        this.count.textContent = template
          .replace('[count]', String(this.message.value.length))
          .replace('[max]', this.dataset.max || String(this.message.maxLength));
      }

      setStatus(text, state = '') {
        if (!this.status) return;
        this.status.textContent = text;
        if (state) this.status.dataset.state = state;
        else delete this.status.dataset.state;
      }

      async restore() {
        try {
          const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
          const attrs = cart.attributes || {};
          if (attrs['Gift order'] === 'Yes') {
            this.check.checked = true;
            this.message.value = attrs['Gift message'] || '';
          }
        } catch (error) {
          // Leave the default unchecked state; nothing was lost.
        }
        this.render();
      }

      queueSave(delay) {
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.save(), delay);
      }

      async save() {
        this.request?.abort();
        this.request = new AbortController();
        const gift = this.check.checked;
        const text = gift ? this.message.value.trim() : '';
        if (gift) this.setStatus(this.dataset.msgSaving || '');
        try {
          const response = await fetch(`${root}cart/update.js`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ attributes: { 'Gift order': gift ? 'Yes' : '', 'Gift message': text } }),
            signal: this.request.signal,
          });
          if (!response.ok) throw new Error('save failed');
          // Only confirm a message once there is one; ticking the box alone just opens the field.
          if (gift) this.setStatus(text ? this.dataset.msgSaved || '' : '');
          else this.setStatus('');
        } catch (error) {
          if (error.name === 'AbortError') return;
          this.setStatus(this.dataset.msgError || '', 'error');
        }
      }
    }
    customElements.define('gp-gift-options', GpGiftOptions);
  }

  // Money text such as "$90.00": Shopify keeps two decimals, so the digits are cents.
  const toCents = (text) => parseInt(String(text).replace(/\D/g, ''), 10) || 0;

  /**
   * Selected Kaching bundle tier, or null when no bundle picker is rendered.
   * Kits come from the bar title ("3 kits"), the total from the bar price.
   * @returns {{ cents: number, kits: number, priceText: string } | null}
   */
  // "pouches" -> "pouch", "kits" -> "kit", "boxes" -> "box".
  const singular = (w) => w.replace(/(ch|sh|x)es$/, '$1').replace(/s$/, '');

  const plural = (w) => (/(ch|sh|x)$/.test(w) ? w + 'es' : w + 's');

  function bundleSelection() {
    const bar = document.querySelector('kaching-bundle .kaching-bundles__bar--selected');
    if (!bar || bar.getBoundingClientRect().height === 0) return null;
    const priceEl = bar.querySelector('.kaching-bundles__bar-price');
    const titleEl = bar.querySelector('.kaching-bundles__bar-title') || bar;
    // Titles read "1 kit", "2 pouches"...: the leading number is the quantity, the word the unit.
    const m = titleEl.textContent.trim().match(/(\d+)\s*([A-Za-z]+)/);
    // "Buy 2 Pouches, Get 1 FREE" ships 3 pouches: count the free ones too.
    const free = titleEl.textContent.match(/get\s+(\d+)\s+free/i);
    const kits = ((m && parseInt(m[1], 10)) || 1) + ((free && parseInt(free[1], 10)) || 0);
    const unitWord = m ? singular(m[2].toLowerCase()) : 'kit';
    // The card's own crossed-out price (garden-center value), when it shows one.
    const fullEl = bar.querySelector('.kaching-bundles__bar-full-price');
    const fullText = fullEl && fullEl.getBoundingClientRect().height ? fullEl.textContent.trim() : '';
    return priceEl ? { cents: toCents(priceEl.textContent), kits, unitWord, priceText: priceEl.textContent.trim(), compareCents: fullText ? toCents(fullText) : 0, compareText: fullText } : null;
  }
  window.gpBundleSelection = bundleSelection;

  /** Quantity on a bundle card, from its title ("Buy 3 Kits" -> 3). */
  function barQty(bar) {
    const t = bar.querySelector('.kaching-bundles__bar-title');
    const m = t && t.textContent.trim().match(/(\d+)/);
    return (m && parseInt(m[1], 10)) || 1;
  }

  /**
   * Add-to-cart text for the selected card from the gift block's atc_label
   * ("Add [count] [unit] to cart • [price]" -> "Add 3 kits to cart • £92.97").
   */
  function applyAtcLabel(bundle) {
    const box = document.querySelector('gp-bundle-gifts');
    const template = box && box.dataset.atcLabel;
    const label = document.querySelector('[data-st-atc-label]');
    if (!template || !label || !bundle) return;
    const unit = bundle.kits > 1 ? plural(bundle.unitWord) : bundle.unitWord;
    const text = template.replace('[count]', bundle.kits).replace('[unit]', unit).replace('[price]', bundle.priceText);
    if (label.textContent.trim() !== text) label.textContent = text;
  }

  const money = (cents) => {
    const currency = (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD';
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', { style: 'currency', currency }).format(cents / 100);
  };

  /**
   * Keeps everything that depends on the selected bundle tier in step:
   * - the main price shows the selected total; a labelled per-kit line appears
   *   for multi-kit tiers and the single-kit compare-at price/badge are hidden
   * - "That's N cents a variety" = total / (varieties x kits), rounded up
   * - gift tiles show locked / included for the selected tier
   * - gift tiles and gift options sit directly under the bundle picker
   * Reads the bundle app's own selected bar; never touches the cart.
   */
  function initBundleSync() {
    const price = document.querySelector('[data-st-price]');
    if (!price || price.dataset.gpBound) return;
    price.dataset.gpBound = 'true';
    const line = document.querySelector('[data-gp-ppv]');
    const varieties = line ? parseInt(line.dataset.varieties, 10) : 0;
    const template = line ? line.dataset.template || '' : '';
    const row = price.closest('.st_pi-price-row') || price.parentElement;
    const compare = row && row.querySelector('[data-st-compare]');
    const badge = row && row.querySelector('[data-st-sale-badge]');
    const single = price.textContent.trim();
    // Compare-at and its percentage pill scale with the kit count (compare-at × kits).
    const singleCompare = compare ? compare.textContent.trim() : '';
    const compareCents = singleCompare ? toCents(singleCompare) : 0;
    const badgeTemplate = badge ? badge.textContent.trim() : '';
    let unit = null;
    if (row) {
      unit = document.createElement('span');
      unit.className = 'gp-unit-price';
      unit.hidden = true;
      unit.setAttribute('data-gp-unit', '');
      row.after(unit);
    }
    let syncing = false;

    const update = () => {
      if (syncing) return;
      syncing = true;
      const bundle = bundleSelection();
      const kits = bundle ? bundle.kits : 1;
      const total = bundle ? bundle.cents : toCents(single);

      // Main price: the selected offer total; unit price labelled per kit.
      const shown = bundle ? bundle.priceText : single;
      if (price.textContent.trim() !== shown) price.textContent = shown;
      if (compare && compareCents) {
        const compareText = kits > 1 ? money(compareCents * kits) : singleCompare;
        if (compare.textContent.trim() !== compareText) compare.textContent = compareText;
      }
      if (badge && compareCents && total && /\d/.test(badgeTemplate)) {
        const percent = Math.round((1 - total / (compareCents * kits)) * 100);
        const badgeText = badgeTemplate.replace(/\d+/, String(percent));
        if (badge.textContent.trim() !== badgeText) badge.textContent = badgeText;
      }
      // No compare-at on the product: show the selected card's crossed-out price
      // and the saving as a pill, so the main price keeps its discount context.
      if (compare && !compareCents) {
        const show = !!(bundle && bundle.compareCents > total && total);
        compare.style.display = show ? '' : 'none';
        if (show && compare.textContent.trim() !== bundle.compareText) compare.textContent = bundle.compareText;
        if (badge) {
          const percent = show ? Math.round((1 - total / bundle.compareCents) * 100) : 0;
          badge.classList.toggle('is-visible', percent > 0);
          const badgeText = percent > 0 ? `Save ${percent}%` : '';
          if (badge.textContent.trim() !== badgeText) badge.textContent = badgeText;
        }
      }
      if (unit) {
        // Always shown (1 kit too), so the line under the price never leaves a gap
        // or shifts the layout when the bundle changes.
        unit.hidden = !total;
        if (total) unit.textContent = `${money(Math.round(total / kits))} per ${(bundle && bundle.unitWord) || 'kit'}`;
      }

      if (line && varieties && total) {
        // A dollar or more reads as money ("$1.00 a variety"), never "100 cents".
        const per = Math.ceil(total / (varieties * kits));
        let text = per >= 100
          ? template.replace(/\[cents\]\s*(?:cents|¢|p)?/, () => money(per))
          : template.replace('[cents]', () => String(per));
        // Several kits do not make more distinct varieties: say what the figure covers.
        if (kits > 1) text = text.replace(/.$/, '') + ` (${kits} ${plural((bundle && bundle.unitWord) || 'kit')}).`;
        if (line.textContent !== text) line.textContent = text;
        // Same figure in the benefit list: "43 cents" highlighted, then "a variety (2 kits)".
        const item = document.querySelector('[data-gp-ppv-item]');
        if (item) {
          const c0 = item.querySelector('[data-gp-ppv-cents]');
          // " cents" in the US, "p" in the UK (set by the theme from the store currency).
          const minor = (c0 && c0.dataset.gpMinor) || ' cents';
          const centsText = per >= 100 ? money(per) : `${per}${minor}`;
          const restText = kits > 1 ? `a variety (${kits} ${plural((bundle && bundle.unitWord) || 'kit')})` : 'a variety';
          const c = item.querySelector('[data-gp-ppv-cents]');
          const r = item.querySelector('[data-gp-ppv-rest]');
          if (c && c.textContent !== centsText) c.textContent = centsText;
          if (r && r.textContent !== restText) r.textContent = restText;
        }
      }

      updateGifts(bundle ? kits : 0);
      applyBarCopy();
      applyRenewalTerms(bundle);
      applyAtcLabel(bundle);
      document.dispatchEvent(new CustomEvent('gp:bundle-change', { detail: bundle }));
      syncing = false;
    };

    new MutationObserver(update).observe(price, { childList: true, characterData: true, subtree: true });

    // The bundle app renders after load and toggles the selected bar's class.
    let finder = null;
    const watchBundle = () => {
      const host = document.querySelector('kaching-bundle .kaching-bundles__bars');
      if (!host) return false;
      finder?.disconnect();
      if (host.dataset.gpWatched) return true;
      host.dataset.gpWatched = 'true';
      // childList too: Kaching can re-render the cards, which drops the perk lines.
      new MutationObserver(update).observe(host.closest('kaching-bundle') || host, { attributes: true, attributeFilter: ['class', 'aria-checked'], childList: true, characterData: true, subtree: true });
      host.addEventListener('change', update);
      // A real shopper click means their choice wins over the default.
      host.addEventListener('pointerdown', () => { pickerTouched = true; }, { once: true });
      host.addEventListener('keydown', () => { pickerTouched = true; }, { once: true });
      setTimeout(applyDefaultBar, 300);
      // Brief order: bundle picker, free gifts, gift options, then the button.
      // With "Show free gifts under the button" on, the gifts move below the button instead.
      const picker = host.closest('kaching-bundle');
      const gifts = document.querySelector('gp-bundle-gifts');
      const giftNote = document.querySelector('gp-gift-options');
      const giftsBelow = gifts && gifts.dataset.giftsBelowButton === 'true';
      let anchor = picker;
      for (const el of giftsBelow ? [giftNote] : [gifts, giftNote]) {
        if (!el) continue;
        if (anchor.nextElementSibling !== el) anchor.after(el);
        anchor = el;
      }
      if (giftsBelow) {
        const form = picker.parentElement.querySelector('form.st_pi-form') || document.querySelector('form.st_pi-form');
        if (form && form.nextElementSibling !== gifts) form.after(gifts);
      }
      // Merchant line above the cards (e.g. "Low Stock: Order Soon!"), between two rules.
      const headingText = gifts && gifts.dataset.pickerHeading;
      if (headingText && picker && !document.querySelector('.gp-picker-heading')) {
        const heading = document.createElement('p');
        heading.className = 'gp-picker-heading';
        heading.textContent = headingText;
        picker.before(heading);
      }
      update();
      return true;
    };
    if (!watchBundle()) {
      finder = new MutationObserver(watchBundle);
      finder.observe(document.body, { childList: true, subtree: true });
      setTimeout(() => finder.disconnect(), 20000);
    }
    update();
  }

  const readList = (box, key) => {
    try {
      const list = JSON.parse(box.dataset[key] || 'null');
      return Array.isArray(list) ? list : null;
    } catch (error) {
      return null;
    }
  };

  /** Sets an element's own first text node, leaving Kaching's comment markers in place. */
  const setOwnText = (el, text) => {
    const node = [...el.childNodes].find((n) => n.nodeType === Node.TEXT_NODE);
    if (node) {
      if (node.data !== text) node.data = text;
    } else {
      el.prepend(document.createTextNode(text));
    }
  };

  /**
   * Bundle card copy from the review mockup, per product, passed on
   * <gp-bundle-gifts data-tabs data-titles data-perks> (gardenpack.bundle_tabs,
   * bundle_titles, bundle_perks):
   * - a tab across the top of each card (replaces Kaching's own label)
   * - the card title ("Buy 2 Kits"; the number stays first for bundleSelection)
   * - "Save $X" from the card's own compare-at and price, in place of the subtitle
   * - a green perk line under the price
   * Text only; Kaching's markup, prices and selection are untouched.
   */
  function applyBarCopy() {
    const box = document.querySelector('gp-bundle-gifts');
    if (!box) return;
    const tabs = readList(box, 'tabs');
    const titles = readList(box, 'titles');
    const perks = readList(box, 'perks');
    const strip = box.dataset.savingsStyle === 'strip';
    // Strip style: the saving moves into a strip across the card foot, so no subtitle line.
    const saveLine = strip ? '' : box.dataset.saveLine || '';
    const picker = document.querySelector('kaching-bundle');
    if (picker) picker.classList.toggle('gp-has-tabs', Boolean(tabs));
    if (picker) picker.classList.toggle('gp-bars--strip', strip);
    const allBars = [...document.querySelectorAll('kaching-bundle .kaching-bundles__bars:not(.kaching-bundles__subscriptions) > .kaching-bundles__bar')];
    const firstPrice = allBars[0] && allBars[0].querySelector('.kaching-bundles__bar-price');
    const singleCents = firstPrice && barQty(allBars[0]) === 1 ? toCents(firstPrice.textContent) : 0;
    document.querySelectorAll('kaching-bundle .kaching-bundles__bars:not(.kaching-bundles__subscriptions) > .kaching-bundles__bar').forEach((bar, i) => {
      const wrapper = bar.querySelector('.kaching-bundles__bar-wrapper');
      const main = bar.querySelector('.kaching-bundles__bar-main');
      if (!wrapper || !main) return;

      if (tabs && tabs[i]) {
        let tab = wrapper.querySelector(':scope > .gp-bar-tab');
        if (!tab) {
          tab = document.createElement('div');
          tab.className = 'gp-bar-tab';
          wrapper.prepend(tab);
        }
        // "-" = no tab on this card; an empty, invisible tab keeps the cards level.
        const tabText = tabs[i] === '-' ? '' : tabs[i];
        tab.classList.toggle('gp-bar-tab--empty', !tabText);
        if (tab.textContent !== tabText) tab.textContent = tabText;
      }

      const title = bar.querySelector('.kaching-bundles__bar-title');
      if (title && titles && titles[i]) setOwnText(title, titles[i]);

      let subtitle = bar.querySelector('.kaching-bundles__bar-subtitle');
      // Cards without a subtitle in the bundle app get one for the savings line.
      if (!subtitle && saveLine) {
        const firstLine = bar.querySelector('.kaching-bundles__bar-first-line');
        if (firstLine) {
          subtitle = document.createElement('div');
          subtitle.className = 'kaching-bundles__bar-subtitle gp-bar-subtitle';
          firstLine.after(subtitle);
        }
      }
      const priceEl = bar.querySelector('.kaching-bundles__bar-price');
      const fullEl = bar.querySelector('.kaching-bundles__bar-full-price');
      // Saving against the card's own crossed-out price; when the card shows none
      // (UK: no "was" prices), against buying the same number of single units.
      const fullShown = fullEl && fullEl.getBoundingClientRect().height ? fullEl : null;
      if (saveLine && subtitle && priceEl) {
        let saved = 0;
        if (fullShown) saved = toCents(fullShown.textContent) - toCents(priceEl.textContent);
        else if (singleCents && i > 0) saved = singleCents * barQty(bar) - toCents(priceEl.textContent);
        if (saved > 0) {
          // "[kits]" is the card's own quantity and word ("1 Kit", "2 Pouches"), so every card reads "2 Kits total | Save £x".
          const tm = (title ? title.textContent.trim() : '').match(/(\d+)\s*([A-Za-z]+)/);
          const q = barQty(bar);
          const kits = tm ? tm[1] + ' ' + tm[2] : q + (q === 1 ? ' Kit' : ' Kits');
          setOwnText(subtitle, saveLine.replace('[kits]', kits).replace('[amount]', money(saved)));
        }
      }

      if (perks && perks[i]) {
        let perk = main.querySelector(':scope > .gp-bar-perk');
        if (!perk) {
          perk = document.createElement('div');
          perk.className = 'gp-bar-perk';
          main.appendChild(perk);
        }
        if (perk.textContent !== perks[i]) perk.textContent = perks[i];
      }

      if (strip && priceEl) applyStrip(bar, wrapper, main, priceEl, fullShown, singleCents, i);
    });
  }

  /**
   * Strip savings style: the price per unit under the card price ("$29.16 / pouch",
   * free units counted) and a "You save $X · Y% off" strip across the card foot.
   */
  function applyStrip(bar, wrapper, main, priceEl, fullShown, singleCents, i) {
    const price = toCents(priceEl.textContent);
    const title = bar.querySelector('.kaching-bundles__bar-title');
    const titleText = title ? title.textContent : '';
    const tm = titleText.match(/(\d+)\s*([A-Za-z]+)/);
    const free = titleText.match(/get\s+(\d+)\s+free/i);
    const units = barQty(bar) + ((free && parseInt(free[1], 10)) || 0);
    const unitWord = tm ? singular(tm[2].toLowerCase()) : 'kit';

    let per = main.querySelector(':scope > .gp-bar-per');
    if (!per) {
      per = document.createElement('div');
      per.className = 'gp-bar-per';
    }
    const perk = main.querySelector(':scope > .gp-bar-perk');
    if (perk && per.nextElementSibling !== perk) main.insertBefore(per, perk);
    else if (!perk && per.parentElement !== main) main.appendChild(per);
    const perText = price && units ? `${money(Math.round(price / units))} / ${unitWord}` : '';
    if (per.textContent !== perText) per.textContent = perText;
    per.hidden = !perText;

    let saved = 0;
    let full = 0;
    if (fullShown) {
      full = toCents(fullShown.textContent);
      saved = full - price;
    } else if (singleCents && i > 0) {
      full = singleCents * barQty(bar);
      saved = full - price;
    }
    let foot = wrapper.querySelector(':scope > .gp-bar-strip');
    if (!foot) {
      foot = document.createElement('div');
      foot.className = 'gp-bar-strip';
      wrapper.appendChild(foot);
    }
    foot.hidden = !(saved > 0);
    if (saved > 0) {
      const html = `You save ${money(saved)}<span>${Math.round((saved / full) * 100)}% off</span>`;
      if (foot.innerHTML !== html) foot.innerHTML = html;
    }
  }

  /**
   * Selects the merchant's default bundle card (data-default-bar, 1-based) once
   * per page load, before the shopper has touched the picker. Uses a normal
   * click so the bundle app updates its own state and the cart line.
   */
  let defaultApplied = false;
  let pickerTouched = false;
  function applyDefaultBar() {
    if (defaultApplied || pickerTouched) return;
    const box = document.querySelector('gp-bundle-gifts');
    const want = box ? parseInt(box.dataset.defaultBar, 10) : 0;
    const bars = [...document.querySelectorAll('kaching-bundle .kaching-bundles__bars:not(.kaching-bundles__subscriptions) > .kaching-bundles__bar')];
    if (!want || bars.length < want) return;
    defaultApplied = true;
    const bar = bars[want - 1];
    if (bar.classList.contains('kaching-bundles__bar--selected')) return;
    bar.querySelector('.kaching-bundles__bar-main')?.click();
  }

  /**
   * Renewal terms under the bundle app's subscription checkbox while it is ticked
   * ("Renews every 3 months at $119.00 until you cancel..."), from the block's
   * renewal_terms setting ([price] = the selected card's price).
   */
  function applyRenewalTerms(bundle) {
    const box = document.querySelector('gp-bundle-gifts');
    const template = box && box.dataset.renewalTerms;
    const wrapper = document.querySelector('kaching-bundle .kaching-bundles__subscriptions-wrapper');
    if (!template || !wrapper) return;
    let note = wrapper.nextElementSibling;
    if (!note || !note.classList.contains('gp-renewal-terms')) {
      note = document.createElement('p');
      note.className = 'gp-renewal-terms';
      wrapper.after(note);
    }
    const ticked = wrapper.querySelector('[aria-checked="true"]');
    note.hidden = !(ticked && bundle);
    if (!bundle) return;
    const text = template.replace('[price]', bundle.priceText);
    if (note.textContent !== text) note.textContent = text;
  }

  /** @param {number} kits Selected kits, or 0 when no bundle picker is rendered. */
  function updateGifts(kits) {
    const box = document.querySelector('gp-bundle-gifts');
    if (!box) return;
    box.hidden = kits === 0;
    if (!kits) return;
    let allOpen = true;
    box.querySelectorAll('.gp-gift-tile').forEach((tile) => {
      const open = kits >= parseInt(tile.dataset.unlock, 10);
      tile.dataset.state = open ? 'open' : 'locked';
      if (!open) allOpen = false;
    });
    const total = box.querySelector('[data-gp-gift-total]');
    if (total) {
      total.textContent = box.dataset.giftTotal || '';
      total.hidden = !(allOpen && box.dataset.giftTotal);
    }
  }

  function initSeasonalLabels() {
    const data = document.querySelector('[data-gp-seasonal]');
    if (!data || data.dataset.gpBound) return;
    data.dataset.gpBound = 'true';
    let config;
    try {
      config = JSON.parse(data.textContent);
    } catch (error) {
      return;
    }
    if (!config.selector) return;
    const apply = () => {
      let cards;
      try {
        cards = document.querySelectorAll(config.selector);
      } catch (error) {
        return true;
      }
      if (!cards.length) return false;
      cards.forEach((card, index) => {
        const label = config.labels[index];
        if (!label || card.querySelector('.gp-season-label')) return;
        const tag = document.createElement('span');
        tag.className = 'gp-season-label';
        tag.textContent = label;
        card.appendChild(tag);
      });
      return true;
    };
    if (apply()) return;
    // Bundle apps render after load: watch briefly, then stop.
    const observer = new MutationObserver(() => { if (apply()) observer.disconnect(); });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 15000);
  }

  const init = () => { initBundleSync(); initSeasonalLabels(); };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
})();
